import { CipherView } from "@bitwarden/common/models/view/cipher.view";
import { inject } from "@angular/core";
import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { CipherService } from "@bitwarden/common/abstractions/cipher.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import {
  StoreModelNames,
  StoreVaultCodeByModelName,
  VaultCodeEnum,
} from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { StoreModel } from "@bitwarden/web-vault/app/services/dali/vault-parser/parser.type";
import { Queue } from "@bitwarden/web-vault/app/services/dali/vault.writer.queue";
import {
  BatchData,
  BatchingStrategy,
} from "@bitwarden/web-vault/app/services/dali/vault.writer.strategy";
import { StoreData } from "@bitwarden/web-vault/app/models/store/store.data";
import { CipherType } from "@bitwarden/common/enums/cipherType";
import { Cipher } from "@bitwarden/common/models/domain/cipher";
import { CipherData } from "@bitwarden/common/models/data/cipher.data";
import { Utils } from "@bitwarden/common/misc/utils";

interface VaultModelItem {
  type: StoreModelNames;
  model: StoreModel;
}

type VaultIdPipeVaultCode = string;

/** gloss-back answers a GET for a missing cipher with 400 and this message (get_cipher in
 * src/api/core/ciphers.rs), not 404, and uses the same 400 for "Cipher is not owned by user", so
 * only the message tells "gone" apart. **/
const CIPHER_GONE_MESSAGE = "Cipher doesn't exist";

enum CRUD {
  update = "update",
  create = "create",
  delete = "delete",
}

/**
 * Thrown when a create and/or update only partially completed — one or more create batches, or a
 * whole per-cipher update, failed its server write (offline, expired session, rejected cipher).
 * Mirrors PartialTransactionDeleteError (#4779): a rejected save is the signal a write did not
 * fully land, so callers can tell a partial write apart from a clean full success instead of a
 * resolved call whose result silently omits the dropped models (#4735 round 4 — `commitUpdate`
 * used to swallow its per-cipher failures and let `syncToVault` resolve).
 *
 * `committed` holds everything that DID persist during the whole syncToVault call — the
 * update/create batches that landed plus the deletes, which never fail partially — keyed by vault
 * code, i.e. exactly what `save` would have returned had it not thrown. The store boundary loads it
 * into memory before surfacing the failure, so the persisted models keep their new vid and a retry
 * updates them instead of creating duplicate ciphers (closing the "save discards which models
 * committed" gap). `failed` holds the update/create models that did not persist; the first
 * underlying failure is preserved on `cause`.
 */
export class PartialVaultWriteError extends Error {
  constructor(
    readonly cause: unknown,
    readonly committed: Map<VaultCodeEnum, StoreModel[]>,
    readonly failed: StoreModel[],
  ) {
    super("Vault write partially completed: one or more batches did not persist");
    this.name = "PartialVaultWriteError";
  }
}

export class VaultWriter {
  protected cipherService: CipherService = inject(CipherService);
  protected apiService: ApiService = inject(ApiService);
  protected log: LogService = inject(LogService);

  private toDelete: Queue<VaultModelItem> = new Queue();
  private toUpdate: Queue<VaultModelItem> = new Queue();
  private toCreate: Queue<VaultModelItem> = new Queue();

  async syncToVault(
    type: StoreModelNames,
    models: StoreModel[],
    organizationId?: string,
    collectionIds?: string[],
  ): Promise<Map<VaultCodeEnum, StoreModel[]>> {
    /** Org creates need a collectionId: with none the server rejects the cipher, or (null → POST
     * /ciphers) creates it orphaned in no collection. Checked before anything is queued or written
     * so a failure can't leave a partial write (#4040). Only creates (no vid, not deleted) are
     * checked, so org-context updates/deletes still pass. **/
    if (organizationId && !collectionIds?.length && models.some((m) => !m.vid && !m.del)) {
      throw new Error("Creating an org-owned cipher requires at least one collectionId.");
    }

    // Separate new Model with Update
    for (const rawModel of models) {
      /** oid is a read-only reference recomputed from the cipher; strip whatever a view supplied so it never enters the persisted payload **/
      const { oid: _oid, ...model } = rawModel;

      /** If Flag for DELETE **/
      if (model.vid && model.del) {
        /** Replace by toDelete when batching support is correctly implemented **/
        this.toDelete.enqueue({ type, model });
      }

      /** If Flag as UPDATE **/
      if (model.vid && !model.del) {
        this.toUpdate.enqueue({ type, model });
      }

      /** If Flag as CREATE **/
      if (!model.vid && !model.del) {
        this.toCreate.enqueue({ type, model });
      }

      /** NOT IN Vault and DELETE **/
      if (!model.vid && model.del) {
        this.log.warning(`Try to delete ${model.id} but was never created.`);
      }
    }

    /** Maybe there is edge case to thing of like what if a delete and an update are in the same batch? **/
    /** commitUpdate no longer aborts the whole call on a bad cipher (#4735 round 2) nor silently
     * swallows that failure (#4735 round 4) — it isolates each cipher, keeps going, and reports what
     * failed alongside what succeeded so commitDelete/commitCreate still run below. */
    const { results: updated, failed, firstError } = await this.commitUpdate();

    /** commitDelete has no per-item isolation (a delete either lands or the whole call rejects), so
     * its failure reaches here as a plain Error, not a PartialVaultWriteError. Wrapping every failure
     * this way keeps the committed set reachable by loadCommittedOnPartialWrite regardless of which
     * call failed, so updates that already persisted are loaded into memory and a retry updates them
     * instead of creating duplicate ciphers (#4735 round 6 — a raw Error out of commitDelete used to
     * discard the update's committed state entirely).
     *
     * commitDelete and commitCreate each get their own try/catch (#5373) — they queue from
     * disjoint models (a model is never both `del` and a create), so a rejected delete must not
     * stop the queued creates from being attempted, or they leak into `toCreate` and get replayed
     * (and re-created) on the next syncToVault call. */
    let deleted: Map<VaultCodeEnum, StoreModel[]> = new Map();
    let created: Map<VaultCodeEnum, StoreModel[]> = new Map();
    let error = firstError;
    try {
      deleted = await this.commitDelete();
    } catch (e) {
      error ??= e;
    }
    try {
      created = await this.commitCreate(organizationId, collectionIds);
    } catch (e) {
      if (e instanceof PartialVaultWriteError) {
        created = e.committed;
        failed.push(...e.failed);
      }
      error ??= e instanceof PartialVaultWriteError ? e.cause : e;
    }

    /** Generate the Map containing the new updated system properties on the model. **/
    /** This only apply for created model and updated model **/
    const committed = this.mergeResults(created, updated, deleted);
    /** A model can land in `failed` with no accompanying error — e.g. commitUpdate's
     * updateOriginalCipherArray logs and continues, without throwing, when a queued model's id
     * isn't in the stored cipher (a stale vid). Gating on `error` alone let that resolve as a
     * clean success (#4735 round 7); commitCreate already throws on failed.length alone, so this
     * matches it. */
    if (error || failed.length) {
      throw new PartialVaultWriteError(error, committed, failed);
    }

    return committed;
  }

  /** Merge per-vault-code result maps into one. Spreading Maps with the same key overwrites, losing
   * created/updated models when both exist for a type (e.g. 2 updates + 1 create for Account), so
   * append per key instead. */
  private mergeResults(
    ...maps: Array<Map<VaultCodeEnum, StoreModel[]>>
  ): Map<VaultCodeEnum, StoreModel[]> {
    const merged = new Map<VaultCodeEnum, StoreModel[]>();
    for (const map of maps) {
      for (const [vaultCode, models] of map) {
        merged.set(vaultCode, [...(merged.get(vaultCode) ?? []), ...models]);
      }
    }
    return merged;
  }

  private async commitDelete(): Promise<Map<VaultCodeEnum, StoreModel[]>> {
    const results: VaultModelItem[] = [];

    const allItemsToDelete = this.toDelete.takeAll();
    const modelType = allItemsToDelete[0]?.type;
    const vaultItemMap: Map<string, StoreModel[]> = this.groupItemsByVid(allItemsToDelete);

    /** Ciphers whose notes we could not read are collected here and re-thrown after the loop so the
     * store's save() rejects and keeps the in-memory models — a bare skip would resolve the delete as
     * a success while the rows are still on the server. Readable vids still commit before the throw. **/
    const unreadableVids: string[] = [];
    /** Ciphers missing from local state that we couldn't confirm gone on the server either. Same
     * treatment as unreadableVids: skip them and reject after the loop. **/
    const unverifiedVids: string[] = [];

    for (const [vid, items] of vaultItemMap) {
      /** Decide from what the cipher actually holds, not a cached count. A stale vid — e.g. a model
       * in memory still pointing here after its row moved to an overflow cipher — must never let a
       * single-row delete wipe a cipher that still holds other rows, and a delete of the last
       * survivors must delete the cipher instead of orphaning an empty one. **/
      let cipherView = await this.cipherService.getCipherView(vid);

      /** getCipherView returns null when the id isn't a GUID or the cipher isn't in local state.
       * That's a cache miss, not proof the cipher is gone: deleting it blind would wipe every row
       * we weren't asked to delete (#6057). Ask the server. If it's gone there is nothing to delete;
       * if it's still there, carry on below with the server's copy. **/
      if (cipherView == null) {
        try {
          cipherView = await this.getCipherViewFromServer(vid);
        } catch (e) {
          this.log.error(
            `commitDelete: could not check cipher ${vid} on the server; skipping delete.`,
          );
          this.log.error(e);
          unverifiedVids.push(vid);
          continue;
        }
        if (cipherView == null) {
          this.log.warning(`commitDelete: cipher ${vid} is already gone from the server.`);
          continue;
        }
      }

      /** Unreadable notes parse to null — an undecryptable cipher decrypts to a marker string, corrupt
       * notes parse to a non-array, and a MAC-/encType-mismatch decrypt (or genuinely absent notes) arrives
       * as literal null. We can't tell what such a cipher holds, so we must not mistake it for empty and
       * wipe rows we merely couldn't read. Collect the vid and throw after the loop rather than skipping
       * silently: a bare `continue` would let save() resolve, and the store drops the in-memory model on a
       * resolved save — reporting a delete that never happened while the row survives on the server. **/
      const stored = this.parseCipherItems(cipherView.notes);
      if (stored === null) {
        /** A literal-null decrypt is ambiguous from the CipherView alone: either a notes EncString that
         * decrypted to null (unreadable — a MAC/encType mismatch, which decryptToUtf8 signals by returning
         * null without throwing, so EncString.decrypt never assigns its marker), or a genuinely notes-less
         * cipher (no notes EncString at all) that a stale vid points at. dali always writes a notes
         * EncString, so a notes-less cipher is not one of ours and holds none of our rows. The raw Cipher
         * tells them apart. Treat the notes-less cipher as a stale vid: skip it — do not delete an unrelated
         * cipher, and do not reject the whole save forever; its queued ids resurface, logged, on the next
         * load. Anything else (a present-but-unreadable notes EncString, or corrupt/non-array notes) is
         * refused so save() rejects and the store keeps its models. **/
        if (cipherView.notes == null) {
          const rawCipher = await this.cipherService.get(vid);
          if (rawCipher?.notes == null) {
            this.log.warning(
              `commitDelete: cipher ${vid} has no notes; queued id(s) not found there (stale vid?); skipping.`,
            );
            continue;
          }
        }
        this.log.error(
          `commitDelete: cipher ${vid} notes unreadable; skipping delete to avoid data loss.`,
        );
        unreadableVids.push(vid);
        continue;
      }

      /** Match survivors the way updateOriginalCipherArray does — a persisted row may be keyed by
       * `_id` rather than `id`. Matching on `id` alone leaves such a row unremoved while the caller
       * is still told it was deleted. Track which queued ids actually matched a stored row so a
       * genuine delete can be told apart from a stale vid whose row is no longer in this cipher. **/
      const matchedIds = new Set<string>();
      const remaining = stored.filter((org) => {
        const orgId = org.id ? org.id : (org as { _id?: string })._id;
        const hit = items.find((item) => item.id === orgId);
        if (hit) {
          matchedIds.add(hit.id);
          return false;
        }
        return true;
      });

      /** Some queued ids matched a stored row; any that didn't are unresolved — a stale vid pointing
       * here after the row moved (e.g. a split relocated it to an overflow cipher). Surface them and
       * keep them out of the reported deletions. Emitted before the branches below so a *full* delete
       * of the matched rows (remaining empty) still logs the stale straggler instead of dropping it
       * unremarked. Gated on `matchedIds.size > 0`: when nothing matched at all it is either a legit
       * empty-cipher cleanup (remaining 0 → deleted below) or a pure stale group (its own message
       * below), neither of which is a mixed straggler. **/
      const unresolved = items.filter((item) => !matchedIds.has(item.id));
      if (matchedIds.size > 0 && unresolved.length > 0) {
        this.log.warning(
          `commitDelete: queued id(s) [${unresolved
            .map((item) => item.id)
            .join(", ")}] not found in cipher ${vid}; not reported as deleted (stale vid?).`,
        );
      }

      if (remaining.length === 0) {
        /** Nothing is left after removing the matched rows. If nothing matched at all, this is a stale vid
         * that landed on an already-empty ("[]") cipher: the straggler warning above is gated on
         * matchedIds.size > 0, and the all-stale warning below sits past this continue, so surface the
         * unresolved id(s) here rather than dropping them silently while the empty cipher is cleaned up. **/
        if (matchedIds.size === 0 && unresolved.length > 0) {
          this.log.warning(
            `commitDelete: queued id(s) [${unresolved
              .map((item) => item.id)
              .join(
                ", ",
              )}] not found in cipher ${vid}; deleting the empty cipher, not reporting them deleted (stale vid?).`,
          );
        }
        await this.cipherService.deleteWithServer(vid);
        continue;
      }

      /** None of the queued ids were actually in this cipher. This happens with a stale vid — e.g. a
       * split relocated the row to the overflow cipher — so rewriting the identical notes would be a
       * no-op that also mis-reports every surviving row as deleted. Leave the cipher untouched and
       * report nothing for it. Decided from the matched set, not `remaining.length === stored.length`,
       * so a *mixed* group (some queued ids here, some stale) still falls through to the update below
       * instead of silently rewriting the cipher and dropping the stale rows unremarked. **/
      if (matchedIds.size === 0) {
        this.log.warning(
          `commitDelete: none of the queued ids were found in cipher ${vid}; skipping no-op update (stale vid?).`,
        );
        continue;
      }

      await this.updateCipherAndSend(cipherView, remaining);
      remaining.forEach((rem) => {
        results.push({ type: modelType, model: rem });
      });
    }

    /** At least one cipher's notes were unreadable. Reject so the store's save() does not resolve — a
     * resolved save drops the in-memory models unconditionally, which would report the rows as deleted
     * while they are still on the server. Readable vids above have already committed; the next
     * successful sync reconciles them. **/
    if (unreadableVids.length > 0) {
      throw new Error(
        `commitDelete: cipher(s) [${unreadableVids.join(", ")}] have unreadable notes; delete not applied.`,
      );
    }
    if (unverifiedVids.length > 0) {
      throw new Error(
        `commitDelete: cipher(s) [${unverifiedVids.join(", ")}] could not be checked on the server; delete not applied.`,
      );
    }

    return this.itemsToMap(results);
  }

  /** Fetch a cipher missing from local state from the server and cache it, or null when the vid
   * isn't a GUID or the server says it doesn't exist. Any other failure (offline, no access)
   * rethrows: we can't tell. **/
  private async getCipherViewFromServer(vid: string): Promise<CipherView | null> {
    /** Server ids are GUIDs, so a non-GUID vid can't be there. **/
    if (!Utils.isGuid(vid)) {
      return null;
    }
    try {
      const response = await this.apiService.getCipher(vid);
      await this.cipherService.upsert(new CipherData(response));
    } catch (e) {
      /** capSend rejects an HTTP error with the raw response ({ status, data }), not an
       * ErrorResponse. Only transport failures are ErrorResponse (status 0). **/
      if (e?.status === 400 && e.data?.Message === CIPHER_GONE_MESSAGE) {
        return null;
      }
      throw e;
    }
    return this.cipherService.getCipherView(vid);
  }

  /** Parse a cipher's notes into its stored rows, or null when they can't be read as a JSON array.
   * An undecryptable cipher decrypts to a marker string (EncString.decrypt swallows the failure) and
   * corrupt notes may parse to a non-array; either way we don't know what the cipher holds, so
   * commitDelete treats it as unreadable and refuses to delete. Notes that legitimately parse to []
   * return [] (an empty cipher that should still be removed), distinct from this null "unreadable"
   * signal. **/
  private parseCipherItems(notes: string | null | undefined): StoreData[] | null {
    /** Empty or absent notes are NOT a legitimate empty cipher and must not be treated as one. encryptCipher
     * — the only writer of dali notes — always JSON.stringifies an array, so an empty dali cipher's notes are
     * the string "[]" (handled below, parses to []). A null/"" value instead means the notes could not be
     * read: most importantly a MAC-/encType-mismatch decrypt, where decryptToUtf8 returns null WITHOUT
     * throwing, so EncString.decrypt never assigns its "[error: cannot decrypt]" marker and the notes arrive
     * as literal null. Classing that as [] would let a single-row delete wipe every row of a cipher we
     * simply could not read. Return the null "unreadable" signal instead; commitDelete refuses to delete
     * (and disambiguates a genuinely notes-less cipher behind a stale vid from a truly unreadable one via
     * the raw Cipher). **/
    if (notes == null || notes.trim() === "") {
      return null;
    }
    try {
      const parsed = JSON.parse(notes);
      return Array.isArray(parsed) ? (parsed as StoreData[]) : null;
    } catch (e) {
      this.log.error("commitDelete: could not parse cipher notes as JSON; treating as unreadable.");
      this.log.error(e);
      return null;
    }
  }

  private groupItemsByVid(allItemsToDelete: VaultModelItem[]) {
    const itemsVidMap: Map<string, StoreModel[]> = new Map();
    allItemsToDelete.forEach((item) => {
      if (itemsVidMap.has(item.model.vid)) {
        itemsVidMap.get(item.model.vid).push(item.model);
      } else {
        itemsVidMap.set(item.model.vid, [item.model]);
      }
    });

    return itemsVidMap;
  }

  private async commitCreate(
    organizationId?: string,
    collectionIds?: string[],
  ): Promise<Map<VaultCodeEnum, StoreModel[]>> {
    const results: Map<VaultCodeEnum, StoreModel[]> = new Map<VaultCodeEnum, StoreModel[]>();

    /** Unqueue and process the batches **/
    const takeItemFromQueue: VaultModelItem[] = this.toCreate.takeAll();

    /** Group by Vault cipher Code **/
    const createMap = this.itemsToMap(takeItemFromQueue);

    /** Track failed batches so a per-batch failure isolates that batch (one bad batch must not abort
     * the sibling batches or vault codes, matching commitUpdate #4735) yet still surfaces to the
     * caller: syncToVault/save must reject on any create failure so "save resolved" keeps meaning
     * "everything persisted" (#4908), instead of returning a result array silently missing them. */
    const failed: StoreModel[] = [];
    let firstError: unknown;

    for (const [vaultCode, models] of createMap.entries()) {
      results.set(vaultCode, []);
      const batches: Array<Array<StoreData>> = BatchingStrategy.getBatches(models, false);
      for (const batch of batches) {
        try {
          /** Send the batch to the server **/
          const cipherView = new CipherView();
          cipherView.name = `${vaultCode}-${crypto.randomUUID()}`;
          if (organizationId) {
            cipherView.organizationId = organizationId;
            cipherView.collectionIds = collectionIds;
          }

          const cipher = await this.encryptAndSend(cipherView, batch);

          /** Set system properties on the newly created batch **/
          results.get(vaultCode).push(...this.setSystemProperties(batch, cipher, CRUD.create));
        } catch (e) {
          this.log.error("Dali Error: Something went wrong when saving the batch");
          this.log.error(e);
          /* Ids and size only: the batch holds the user's financial data. An oversized item shows
           * up here, with the server's own "exceeds the maximum encrypted value length" error
           * logged just above (#5805). */
          this.log.error(
            `Dali: rejected batch [${batch.map((m) => m.id).join(", ")}] (${JSON.stringify(batch).length} chars)`,
          );
          firstError ??= e;
          failed.push(...batch);
        }
      }
    }

    if (failed.length > 0) {
      throw new PartialVaultWriteError(firstError, results, failed);
    }

    return results;
  }

  private async commitUpdate(): Promise<{
    results: Map<VaultCodeEnum, StoreModel[]>;
    failed: StoreModel[];
    firstError: unknown;
  }> {
    const results: Map<VaultCodeEnum, StoreModel[]> = new Map<VaultCodeEnum, StoreModel[]>();

    let firstError: unknown;

    /** Unqueue all and restructure for processing **/
    const takeItemFromQueue: VaultModelItem[] = this.toUpdate.takeAll();
    const groupPerVault: Map<VaultIdPipeVaultCode, StoreModel[]> =
      this.groupPerVault(takeItemFromQueue);

    // LOOP All vault id
    for (const [vaultId, models] of groupPerVault.entries()) {
      /** Get the vault id and the type back from the key**/
      const vid = vaultId.split("|")[0] as string;
      const vaultCode = StoreVaultCodeByModelName[
        vaultId.split("|")[1] as StoreModelNames
      ] as VaultCodeEnum;

      if (!results.has(vaultCode)) {
        results.set(vaultCode, []);
      }

      try {
        /** Load original server data **/
        const cipherView = await this.cipherService.getCipherView(vid);
        const original = JSON.parse(cipherView.notes);

        /**
         * The write path must not run per-item version-upgrade chains over `original` - it's the
         * *entire* existing cipher collection, not just the model(s) being saved. Several v0 DTOs
         * are destructive/lossy when re-run over an already-current sibling (e.g.
         * ReferenceDataParser.getParser(0) discards its input and returns an empty default
         * model; category_v0_to_v1 restamps dc/dm to "now"), so upgrading here risks corrupting
         * untouched items. Version upgrades belong to the read path (parseVaultData), which already
         * re-derives each item's version on load. `original` is written back as-is; only the
         * matched model(s) are replaced.
         **/
        this.updateOriginalCipherArray(original, models);

        /** Recalculate the batching if this need to changes **/
        const batches: BatchData = BatchingStrategy.getBatches(original, true);

        /** The first batch stays in the original cipher; the rest overflow into new ciphers. **/
        const primaryBatch = batches.shift();

        /** Create the overflow ciphers *before* the primary PUT (#4365). The PUT truncates the original
         * cipher to the primary batch, so if it ran first and an overflow create then threw, every row in
         * the failed batch — including untouched siblings the caller never queued, which `failed` can't
         * report — would be gone from the server. Doing it in this order, a failure anywhere leaves the
         * original cipher intact: roll back whatever overflow ciphers were already created and rethrow,
         * so the whole group lands in `failed` with nothing lost. **/
        const overflow: Array<{ cipher: CipherView; batch: StoreData[] }> = [];
        try {
          /** Overflow ciphers inherit org context from the parent cipher so overflow from an org-owned
           * cipher doesn't spill into the editing user's personal vault. Only for an org parent, though,
           * mirroring commitCreate (#4364): a personal parent's collectionIds is `[]`, not null, and
           * createWithServer routes on nullness, so copying it sent personal overflow through the
           * org-aware POST /ciphers/create. The array is copied so the overflow cipher's local data
           * doesn't alias the parent's. **/
          for (const batch of batches) {
            const overflowCipherView = new CipherView();
            overflowCipherView.name = `${vaultCode}-${crypto.randomUUID()}`;
            if (cipherView.organizationId) {
              overflowCipherView.organizationId = cipherView.organizationId;
              overflowCipherView.collectionIds = cipherView.collectionIds
                ? [...cipherView.collectionIds]
                : null;
            }

            overflow.push({ cipher: await this.encryptAndSend(overflowCipherView, batch), batch });
          }

          if (primaryBatch) {
            await this.updateCipherAndSend(cipherView, primaryBatch);
          } else {
            this.log.warning(
              `commitUpdate cipher ${cipherView.id} has no batches to send, update dropped.`,
            );
          }
        } catch (e) {
          await this.rollbackOverflowCiphers(overflow.map(({ cipher }) => cipher.id));
          throw e;
        }

        if (primaryBatch) {
          results
            .get(vaultCode)!
            .push(...this.setSystemProperties(primaryBatch, cipherView, CRUD.update));
        }

        for (const { cipher, batch } of overflow) {
          results.get(vaultCode).push(...this.setSystemProperties(batch, cipher, CRUD.update));
        }
      } catch (e) {
        /** One bad cipher must not abort every other vault id queued for update, nor the
         * commitDelete/commitCreate calls chained after commitUpdate in syncToVault (#4735 round
         * 2). But it must not resolve as a silent success either (#4735 round 4) — collect it so
         * syncToVault can still reject once commitDelete/commitCreate have had their turn. **/
        this.log.error(`Dali Error: Something went wrong when updating cipher ${vid}`);
        this.log.error(e);
        firstError ??= e;
      }
    }

    /** Derive failed from what actually persisted rather than pushing the whole queued group in the
     * catch above: a group whose primary batch landed before an overflow write threw would otherwise
     * report the same model as both committed (in results) and failed (#4735 round 6). */
    const persisted = new Set([...results.values()].flat().map((m) => m.id));
    const failed = [...groupPerVault.values()].flat().filter((m) => !persisted.has(m.id));

    return { results, failed, firstError };
  }

  /** Best-effort undo of overflow ciphers created for a commitUpdate group that then failed. The
   * original cipher was never rewritten, so it still holds every row; a rollback that fails only
   * leaves those rows duplicated in an extra cipher, never lost. **/
  private async rollbackOverflowCiphers(cipherIds: string[]) {
    for (const cipherId of cipherIds) {
      try {
        await this.cipherService.deleteWithServer(cipherId);
      } catch (e) {
        this.log.error(
          `commitUpdate: could not roll back overflow cipher ${cipherId}; its rows are duplicated on the server.`,
        );
        this.log.error(e);
      }
    }
  }

  private updateOriginalCipherArray(original: any[], toUpdate: StoreModel[]) {
    for (const model of toUpdate) {
      const replaceIndex = original.findIndex((m) => {
        return m.id ? m.id === model.id : m._id === model.id;
      });
      if (replaceIndex > -1) {
        original[replaceIndex] = model;
      } else {
        this.log.error(`commitUpdate model ${model.id} not in original array.`);
      }
    }
  }

  private groupPerVault(
    takeItemFromQueue: VaultModelItem[],
  ): Map<VaultIdPipeVaultCode, StoreModel[]> {
    const groupPerVaultId = new Map<VaultIdPipeVaultCode, StoreModel[]>();
    for (const item of takeItemFromQueue) {
      const key = `${item.model.vid}|${item.type}`;
      if (groupPerVaultId.has(key)) {
        groupPerVaultId.get(key).push(item.model);
      } else {
        groupPerVaultId.set(key, [item.model]);
      }
    }
    return groupPerVaultId;
  }

  private async encryptCipher(cipherView: CipherView, batchItems: StoreData[]): Promise<Cipher> {
    /**  todo Add a replacer that blacklist vid, del and dc for updated CRUD **/
    cipherView.notes = JSON.stringify(batchItems);
    cipherView.type = CipherType.SecureNote;
    cipherView.favorite = false;

    return await this.cipherService.encrypt(cipherView);
  }
  private async encryptAndSend(
    cipherView: CipherView,
    batchItems: StoreData[],
  ): Promise<CipherView> {
    const encCipher = await this.encryptCipher(cipherView, batchItems);
    await this.cipherService.createWithServer(encCipher);
    cipherView.id = encCipher.id;
    return cipherView;
  }

  private async updateCipherAndSend(
    cipherView: CipherView,
    batchItems: StoreData[],
  ): Promise<CipherView> {
    const encCipher = await this.encryptCipher(cipherView, batchItems);
    await this.cipherService.updateWithServer(encCipher);
    return cipherView;
  }

  private setSystemProperties(models: StoreData[], cipher: CipherView, action: CRUD): StoreData[] {
    return models.map((model) => {
      return {
        ...model,
        vid: cipher.id,
        oid: cipher.organizationId ?? undefined,
        dm: new Date().toISOString(),
        dc: action === CRUD.create ? new Date().toISOString() : model.dc,
      };
    });
  }

  private itemsToMap(items: VaultModelItem[]): Map<VaultCodeEnum, StoreModel[]> {
    const map: Map<VaultCodeEnum, StoreModel[]> = new Map<VaultCodeEnum, StoreModel[]>();
    for (const item of items) {
      if (map.has(StoreVaultCodeByModelName[item.type])) {
        map.get(StoreVaultCodeByModelName[item.type]).push(item.model);
      } else {
        map.set(StoreVaultCodeByModelName[item.type], [item.model]);
      }
    }
    return map;
  }
}
